// What a reseller sells each TLD for (screaming.md → pricing, reseller_tld_prices), shared by the
// API's pricing context and the portal. Each offered term carries two sets of prices:
// `wholesale`, what the reseller pays KilatDomain (for now the registry cost, until price lists
// exist), and `retail`, what the reseller charges its own customers. Retail is never below
// wholesale; the API checks it against the wholesale it reads, so the contract cannot.
import { z } from "zod";
import { Money } from "./money";
import { MAX_YEARS, TldName } from "./tld-pricing";

/** The three things a TLD is priced for, in the order the grid shows them. */
export const PRICE_ACTIONS = ["register", "transfer", "renew"] as const;
export type PriceAction = (typeof PRICE_ACTIONS)[number];

/** A key on the wire: 22 Base62 characters, never a raw UUID (screaming.md rule 19). */
const Base62Id = z.string().regex(/^[0-9A-Za-z]{22}$/);

/** One price per action. Null: not offered (wholesale) or not sold (retail). */
export const Prices = z.object({
  register: Money.nullable(),
  transfer: Money.nullable(),
  renew: Money.nullable(),
});
export type Prices = z.infer<typeof Prices>;

/** One term KilatDomain offers: what the reseller pays for it and what it sells it for. */
export const ResellerTldTerm = z.object({
  years: z.int().min(1).max(MAX_YEARS),
  wholesale: Prices,
  retail: Prices,
});
export type ResellerTldTerm = z.infer<typeof ResellerTldTerm>;

/**
 * One row of the reseller's list, in the reseller's order. `id` is the TLD's own (staff's) id;
 * `terms` holds only the terms KilatDomain offers, by year.
 */
export const ResellerTld = z.object({
  id: Base62Id,
  tld: TldName,
  enabled: z.boolean(),
  terms: z.array(ResellerTldTerm),
});
export type ResellerTld = z.infer<typeof ResellerTld>;

export const ListResellerTldsResponse = z.object({ data: z.array(ResellerTld) });
export const ResellerTldResponse = z.object({ data: ResellerTld });

/** Every retail price of one TLD, replacing what was there: a term left out is not sold. */
export const SetRetailPricesRequest = z.object({
  prices: z
    .array(Prices.extend({ years: z.int().min(1).max(MAX_YEARS) }))
    .max(MAX_YEARS)
    .refine((rows) => new Set(rows.map((row) => row.years)).size === rows.length, "terms_repeated"),
});
export type SetRetailPricesRequest = z.infer<typeof SetRetailPricesRequest>;

export const SetTldEnabledRequest = z.object({ enabled: z.boolean() });
export type SetTldEnabledRequest = z.infer<typeof SetTldEnabledRequest>;
